// Obra › Clima › Historial: lo que pasó en un rango de fechas (temperatura, lluvia, viento, días no aptos),
// cruzado con la actividad de la obra (personas por día del Dashboard IA) y fotos de los días de más lluvia.
import React, { useEffect, useMemo, useState } from 'react';
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceArea, ReferenceLine } from 'recharts';
import { ChevronDown } from 'lucide-react';
import R from './reglasClima';
import { historico, unir, mensajeClima } from './meteo';
import { actividadPorDia, fotoDelDia } from './fotosObra';
import { SkyDefs, SkyBand, SKY_CSS } from '../../IADashboard/clima/Sky';
import { Seccion, Kpi, Semaforo, ESTADOS, mm, grados, diaCorto, Cargando, nivelLluvia } from './ui';

export function rangoPreset(k, hoy) {
  const ayer = R.addDias(hoy, -1);
  if (k === '7') return [R.addDias(hoy, -7), ayer];
  if (k === '30') return [R.addDias(hoy, -30), ayer];
  if (k === '90') return [R.addDias(hoy, -90), ayer];
  if (k === 'mes') return [`${hoy.slice(0, 7)}-01`, hoy];
  if (k === 'mesant') { const d = new Date(Date.UTC(+hoy.slice(0, 4), +hoy.slice(5, 7) - 2, 1)).toISOString().slice(0, 10); return [d, new Date(Date.UTC(+hoy.slice(0, 4), +hoy.slice(5, 7) - 1, 0)).toISOString().slice(0, 10)]; }
  return null;
}
const PRESETS = [['7', '7 días'], ['30', '30 días'], ['90', '90 días'], ['mes', 'Este mes'], ['mesant', 'Mes anterior'], ['custom', 'Elegir fechas']];

/** Carga y evalúa un rango (lo usan Historial y el Reporte PDF). */
export function useRango({ loc, ixFc, U, hoy, desde, hasta }) {
  const [st, setSt] = useState({ cargando: true });
  const [reintento, setReintento] = useState(0); // si falla el servicio del clima, se reintenta solo a los 30 s
  useEffect(() => {
    let vivo = true;
    setSt({ cargando: true });
    const ayer = R.addDias(hoy, -1);
    const pasado = desde <= ayer ? historico(loc, desde, hasta < ayer ? hasta : ayer, hoy) : Promise.resolve(null);
    pasado.then((h) => {
      if (!vivo) return;
      const ix = unir(h, ixFc);
      const evals = [];
      for (let f = desde; f <= hasta && f <= hoy; f = R.addDias(f, 1)) evals.push(R.evaluarDia(ix, f, U));
      setSt({ cargando: false, ix, evals, resumen: R.resumenPeriodo(evals, U) });
    }).catch((e) => { if (!vivo) return; setSt({ cargando: false, error: mensajeClima(e) }); setTimeout(() => vivo && setReintento((n) => n + 1), 30000); });
    return () => { vivo = false; };
  }, [loc, ixFc, U, hoy, desde, hasta, reintento]);
  return st;
}

function GraficoDias({ evals, ix, ia, U }) {
  const uid = 'hist';
  const data = evals.filter((e) => e.estado).map((e, i) => {
    const d = ix.diasMap.get(e.fecha) || {};
    return { idx: i, fecha: e.fecha, mm: e.datos.mmDia || 0, tmax: e.datos.tmaxDia ?? d.tmax, tmin: e.datos.tminDia ?? d.tmin, rafaga: e.datos.rafaga, estado: e.estado, kind: R.tipoTiempo(e.datos.code, e.datos.mmDia, false) || 'nublado', personas: ia?.get(e.fecha)?.pico ?? null };
  });
  const bands = [];
  data.forEach((r, i) => {
    const level = nivelLluvia(r.mm, false);
    const last = bands[bands.length - 1];
    if (last && last.kind === r.kind && last.level === level) last.b = i; else bands.push({ a: i, b: i, kind: r.kind, level, night: false });
  });
  const maxMm = Math.max(5, ...data.map((r) => r.mm));
  const tick = (i) => (data[i] ? `${Number(data[i].fecha.slice(8))}/${Number(data[i].fecha.slice(5, 7))}` : '');
  const Tip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const r = payload[0].payload;
    return (
      <div className="bg-white/95 border border-gray-200 rounded-xl shadow-lg px-3 py-2 text-xs text-gray-700 space-y-0.5">
        <p className="font-semibold text-gray-900 capitalize">{diaCorto(r.fecha)} <Semaforo estado={r.estado} small /></p>
        <p>Máx {grados(r.tmax)} · mín {grados(r.tmin)}</p>
        <p>Lluvia {mm(r.mm)} mm · ráfagas {Math.round(r.rafaga ?? 0)} km/h</p>
        {r.personas != null && <p>👷 {r.personas} personas (pico)</p>}
      </div>
    );
  };
  return (
    <div>
      <style>{SKY_CSS}</style>
      <div style={{ width: '100%', height: 280 }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 10, right: 4, left: -18, bottom: 0 }} accessibilityLayer={false}>
            <defs><SkyDefs uid={uid} /></defs>
            {bands.map((b) => <ReferenceArea key={b.a} yAxisId="t" x1={b.a} x2={b.b} ifOverflow="extendDomain" shape={(p) => <SkyBand {...p} x={p.x - 2} width={p.width + 4} uid={uid} band={b} animate={false} />} />)}
            <CartesianGrid stroke="#e5e7eb" strokeOpacity={0.6} vertical={false} />
            <XAxis dataKey="idx" type="category" tickFormatter={tick} tick={{ fill: '#6b7280', fontSize: 11 }} minTickGap={18} stroke="#e5e7eb" />
            <YAxis yAxisId="t" unit="°" tick={{ fill: '#6b7280', fontSize: 11 }} width={48} domain={['dataMin - 3', 'dataMax + 3']} allowDecimals={false} />
            <YAxis yAxisId="mm" orientation="right" tick={{ fill: '#0369a1', fontSize: 10 }} width={34} domain={[0, Math.ceil(maxMm * 1.6)]} unit="" />
            <Tooltip content={<Tip />} cursor={{ fill: 'rgba(37,99,235,.06)' }} isAnimationActive={false} />
            <Bar yAxisId="mm" dataKey="mm" fill="#0284c7" fillOpacity={0.85} radius={[3, 3, 0, 0]} maxBarSize={18} isAnimationActive={false} />
            <Line yAxisId="t" dataKey="tmax" stroke="#ea580c" strokeWidth={2} dot={false} isAnimationActive={false} connectNulls />
            <Line yAxisId="t" dataKey="tmin" stroke="#2563eb" strokeWidth={2} dot={false} isAnimationActive={false} connectNulls />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      {/* semáforo de cada día debajo del gráfico */}
      <div className="flex gap-px ml-[30px] mr-[38px] mt-1" aria-hidden="true">
        {data.map((r) => <span key={r.fecha} className="flex-1 h-2.5 rounded-sm" style={{ background: ESTADOS[r.estado].hex, opacity: 0.85 }} title={`${diaCorto(r.fecha)}: ${ESTADOS[r.estado].txt}`} />)}
      </div>
      <div style={{ width: '100%', height: 120 }} className="mt-2">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 6, right: 4, left: -18, bottom: 0 }} accessibilityLayer={false}>
            <CartesianGrid stroke="#e5e7eb" vertical={false} />
            <XAxis dataKey="idx" type="category" tickFormatter={tick} tick={{ fill: '#6b7280', fontSize: 10 }} minTickGap={18} stroke="#e5e7eb" />
            <YAxis yAxisId="v" tick={{ fill: '#6b7280', fontSize: 10 }} width={48} allowDecimals={false} />
            <YAxis yAxisId="p" orientation="right" tick={{ fill: '#4f46e5', fontSize: 10 }} width={34} allowDecimals={false} hide={!data.some((r) => r.personas != null)} />
            <Tooltip content={<Tip />} isAnimationActive={false} />
            {data.some((r) => r.personas != null) ? <Bar yAxisId="p" dataKey="personas" fill="#6366f1" fillOpacity={0.6} radius={[3, 3, 0, 0]} maxBarSize={14} isAnimationActive={false} /> : null}
            <Line yAxisId="v" dataKey="rafaga" stroke="#7c3aed" strokeWidth={1.8} dot={false} isAnimationActive={false} connectNulls />
            <ReferenceLine yAxisId="v" y={U.gruaRafaga} stroke="#e11d48" strokeDasharray="4 4" strokeOpacity={0.6} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-gray-600 mt-1 px-1">
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-0.5 bg-orange-600 rounded" />Máxima</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-0.5 bg-blue-600 rounded" />Mínima</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 bg-sky-600 rounded-sm" />Lluvia del día (mm)</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-0.5 bg-violet-600 rounded" />Ráfaga máxima (km/h)</span>
        {data.some((r) => r.personas != null) && <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 bg-indigo-500 rounded-sm" />Personas en la obra (pico)</span>}
      </div>
    </div>
  );
}

/** "Los días de lluvia trabajó X % menos gente" (solo días hábiles con fotos). */
export function cruceLluviaGente(evals, ia) {
  if (!ia || !ia.size) return null;
  const hab = evals.filter((e) => e.estado && e.laborable && ia.has(e.fecha));
  const lluvia = hab.filter((e) => e.datos.mmJ >= 1);
  const seco = hab.filter((e) => e.datos.mmJ < 0.2 && e.estado !== 'no');
  if (lluvia.length < 1 || seco.length < 3) return null;
  const prom = (xs) => xs.reduce((s, e) => s + (ia.get(e.fecha).pico || 0), 0) / xs.length;
  const pl = prom(lluvia), ps = prom(seco);
  return { diasLluvia: lluvia.length, diasSecos: seco.length, personasLluvia: pl, personasSeco: ps, menos: ps > 0 ? Math.round((1 - pl / ps) * 100) : null, sinActividad: lluvia.filter((e) => !ia.get(e.fecha).trabajo).length };
}

function FotosLluvia({ evals, ix, user }) {
  const top = useMemo(() => evals.filter((e) => e.estado && e.datos.mmJ >= 2).sort((a, b) => b.datos.mmJ - a.datos.mmJ).slice(0, 4), [evals]);
  const [fotos, setFotos] = useState({});
  useEffect(() => {
    let vivo = true;
    setFotos({});
    top.forEach((e) => {
      const hs = (ix.porFecha.get(e.fecha) || []).filter((x) => x.h >= 8 && x.h < 19);
      const pico = hs.length ? hs.reduce((a, x) => (x.mm > a.mm ? x : a)) : { h: 12 };
      fotoDelDia(user, e.fecha, pico.h).then((f) => vivo && setFotos((s) => ({ ...s, [e.fecha]: f || 'no' }))).catch(() => vivo && setFotos((s) => ({ ...s, [e.fecha]: 'no' })));
    });
    return () => { vivo = false; };
  }, [top, ix, user?.identifier]); // eslint-disable-line react-hooks/exhaustive-deps
  // Sólo días con foto que de verdad carga: si la cámara no tiene foto de ese día (o la foto no abre), ese día no va.
  // Sin días de lluvia fuerte o sin ninguna foto, la sección entera no se muestra.
  const sinFoto = (fecha) => setFotos((st) => ({ ...st, [fecha]: 'no' }));
  const visibles = top.filter((e) => fotos[e.fecha] !== 'no');
  if (!visibles.length) return null;
  return (
    <Seccion titulo="La obra en los días de más lluvia" sub="Foto de la cámara a la hora en que más llovió">
    <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
      {visibles.map((e) => {
        const f = fotos[e.fecha];
        return (
          <figure key={e.fecha} className="rounded-xl overflow-hidden border border-gray-200 bg-gray-100">
            <div className="aspect-video bg-gray-200 relative">
              {f && f.url ? <img src={f.url} alt={`La obra el ${diaCorto(e.fecha)}`} className="w-full h-full object-cover" loading="lazy" onError={() => sinFoto(e.fecha)} /> : <div className="absolute inset-0 animate-pulse bg-gray-300" />}
              <span className="absolute top-1.5 left-1.5 text-[11px] font-bold bg-sky-700 text-white rounded-full px-2 py-0.5">💧 {mm(e.datos.mmDia)} mm</span>
            </div>
            <figcaption className="px-2 py-1.5 text-[11px] text-gray-700 bg-white capitalize">{diaCorto(e.fecha)}{f && f.ms ? ` · ${new Date(f.ms - 3 * 3600e3).toISOString().slice(11, 16)} h` : ''}</figcaption>
          </figure>
        );
      })}
    </div>
    </Seccion>
  );
}

export default function Historial({ loc, ixFc, U, hoy, user }) {
  const [preset, setPreset] = useState('30');
  const [custom, setCustom] = useState(() => rangoPreset('30', hoy));
  const [desde, hasta] = preset === 'custom' ? custom : rangoPreset(preset, hoy);
  const { cargando, error, ix, evals, resumen } = useRango({ loc, ixFc, U, hoy, desde, hasta });
  const [ia, setIa] = useState(undefined);
  useEffect(() => {
    let vivo = true;
    setIa(undefined);
    actividadPorDia(user, desde, hasta).then((m) => vivo && setIa(m)).catch(() => vivo && setIa(null));
    return () => { vivo = false; };
  }, [user?.identifier, user?.customerId, desde, hasta]); // eslint-disable-line react-hooks/exhaustive-deps
  const cruce = useMemo(() => (evals && ia ? cruceLluviaGente(evals, ia) : null), [evals, ia]);
  const [tabla, setTabla] = useState(false);

  const setFecha = (i, v) => {
    if (!v) return;
    const n = [...custom];
    n[i] = v > hoy ? hoy : v;
    if (n[0] > n[1]) n[1 - i] = n[i];
    if (R.addDias(n[0], 366) < n[1]) n[i === 0 ? 1 : 0] = i === 0 ? R.addDias(n[0], 366) : R.addDias(n[1], -366);
    setCustom(n);
  };

  return (
    <div className="space-y-4 clima-in">
      <div className="flex flex-wrap items-center gap-1.5">
        {PRESETS.map(([k, t]) => (
          <button key={k} type="button" onClick={() => setPreset(k)} className={`text-sm font-semibold rounded-full px-3 py-1.5 border ${preset === k ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'}`}>{t}</button>
        ))}
        {preset === 'custom' && (
          <span className="flex items-center gap-1.5 text-sm">
            <input type="date" value={custom[0]} max={hoy} onChange={(e) => setFecha(0, e.target.value)} className="border border-gray-300 rounded-lg px-2 py-1 text-sm" aria-label="Desde" />
            <span className="text-gray-400">a</span>
            <input type="date" value={custom[1]} max={hoy} onChange={(e) => setFecha(1, e.target.value)} className="border border-gray-300 rounded-lg px-2 py-1 text-sm" aria-label="Hasta" />
          </span>
        )}
      </div>

      {cargando && <Cargando txt="Trayendo el historial del clima…" />}
      {error && <p className="text-sm text-rose-700">{error}</p>}
      {!cargando && !error && resumen && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-2.5">
            <Kpi tono="azul" label="Lluvia total" valor={<>{Math.round(resumen.mm)}<span className="text-sm text-gray-500"> mm</span></>} sub={`Máximo en un día: ${mm(resumen.mmMax)} mm`} />
            <Kpi tono="azul" label="Días de lluvia" valor={resumen.diasLluvia} sub={`de ${resumen.dias} días (1 mm o más)`} />
            <Kpi tono="rojo" label="Hábiles no aptos" valor={resumen.noAptos} sub={`${resumen.noAptosLluvia} por lluvia o tormenta`} />
            <Kpi tono="verde" label="Hábiles aptos" valor={<>{resumen.aptos + resumen.cuidado}<span className="text-sm text-gray-500"> de {resumen.habiles}</span></>} sub={`${resumen.cuidado} con cuidado`} />
            <Kpi tono="violeta" label="Ráfaga máxima" valor={<>{Math.round(resumen.rafagaMax)}<span className="text-sm text-gray-500"> km/h</span></>} />
            <Kpi tono="ambar" label="Temperaturas" valor={<>{grados(resumen.tmax)} <span className="text-gray-400 font-normal">/</span> {grados(resumen.tmin)}</>} sub="máxima y mínima del período" />
          </div>

          <Seccion titulo="Temperatura, lluvia y viento día por día" sub={`Del ${R.fechaLarga(desde)} al ${R.fechaLarga(hasta > hoy ? hoy : hasta)}`}>
            <GraficoDias evals={evals} ix={ix} ia={ia} U={U} />
          </Seccion>

          {cruce && (
            <div className="rounded-2xl border border-indigo-200 bg-gradient-to-r from-indigo-50 to-sky-50 p-4 flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="text-4xl" aria-hidden="true">👷</div>
              <div className="flex-1 min-w-0">
                <p className="text-base font-semibold text-indigo-950">
                  {cruce.menos != null && cruce.menos > 0 ? <>Los días de lluvia trabajó un <b>{cruce.menos} %</b> menos gente</> : <>Los días de lluvia se trabajó igual que los días secos</>}
                </p>
                <p className="text-sm text-indigo-900/80">Promedio de {cruce.personasLluvia.toFixed(1).replace('.', ',')} personas en {cruce.diasLluvia} {cruce.diasLluvia === 1 ? 'día' : 'días'} con lluvia contra {cruce.personasSeco.toFixed(1).replace('.', ',')} en {cruce.diasSecos} días secos.{cruce.sinActividad ? ` ${cruce.sinActividad} ${cruce.sinActividad === 1 ? 'día de lluvia no tuvo' : 'días de lluvia no tuvieron'} actividad.` : ''}</p>
              </div>
              <div className="flex items-end gap-3 h-20 shrink-0" aria-hidden="true">
                {[['Seco', cruce.personasSeco, '#10b981'], ['Lluvia', cruce.personasLluvia, '#0284c7']].map(([t, v, c]) => (
                  <div key={t} className="flex flex-col items-center gap-1 w-12">
                    <div className="w-8 rounded-t-md" style={{ height: `${Math.max(4, (v / Math.max(cruce.personasSeco, cruce.personasLluvia, 1)) * 56)}px`, background: c }} />
                    <span className="text-[10px] font-semibold text-gray-600">{t}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {ia === undefined && user?.identifier && <p className="text-[11px] text-gray-400 px-1">Cruzando con la actividad de la obra…</p>}

          {user?.identifier && <FotosLluvia evals={evals} ix={ix} user={user} />}

          <Seccion titulo="Detalle diario" right={<button type="button" onClick={() => setTabla(!tabla)} className="inline-flex items-center gap-1 text-xs font-semibold text-blue-700"><ChevronDown className={`w-4 h-4 transition-transform ${tabla ? 'rotate-180' : ''}`} />{tabla ? 'Ocultar' : 'Ver tabla'}</button>} bodyClass={tabla ? 'p-0' : 'hidden'}>
            {tabla && (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 text-gray-500 uppercase text-[10px]">
                    <tr><th className="text-left px-3 py-2">Día</th><th className="text-left px-2">Estado</th><th className="text-right px-2">Lluvia (obra)</th><th className="text-right px-2">Lluvia (día)</th><th className="text-right px-2">Máx</th><th className="text-right px-2">Mín</th><th className="text-right px-2">Ráfaga</th>{ia && <th className="text-right px-2">👷</th>}<th className="text-left px-3">Motivo</th></tr>
                  </thead>
                  <tbody>
                    {evals.filter((e) => e.estado).map((e) => (
                      <tr key={e.fecha} className={`border-t border-gray-100 ${!e.laborable ? 'text-gray-400' : 'text-gray-800'}`}>
                        <td className="px-3 py-1.5 whitespace-nowrap capitalize">{diaCorto(e.fecha)}</td>
                        <td className="px-2"><Semaforo estado={e.estado} small /></td>
                        <td className="px-2 text-right tabular-nums">{mm(e.datos.mmJ)}</td>
                        <td className="px-2 text-right tabular-nums">{mm(e.datos.mmDia)}</td>
                        <td className="px-2 text-right tabular-nums">{grados(e.datos.tmaxDia ?? e.datos.tmax)}</td>
                        <td className="px-2 text-right tabular-nums">{grados(e.datos.tminDia ?? e.datos.tmin)}</td>
                        <td className="px-2 text-right tabular-nums">{Math.round(e.datos.rafaga ?? 0)}</td>
                        {ia && <td className="px-2 text-right tabular-nums">{ia.get(e.fecha)?.pico ?? '—'}</td>}
                        <td className="px-3 min-w-[14rem]">{e.estado === 'apto' ? '—' : e.motivos[0].texto}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Seccion>
        </>
      )}
    </div>
  );
}
